import { Fragment, useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "usehooks-ts";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import CollectionSnippet from "components/CollectionSnippet";
import EmptyState from "components/EmptyState";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Input from "components/Input";
import { Tab, Tabs } from "components/Tabs";
import useInfiniteList from "hooks/useInfiniteList";
import { ApiEndpoint } from "utils/baseFetcher";

import CollectionSectionLabel from "../CollectionSectionLabel";
import { type Tab as TabType, AVAILABLE_TABS } from "../CollectionsPageTabs";

import EmptyFollowedCollections from "./EmptyFollowedCollections";
import LoadingPlaceholder from "./LoadingPlaceholder";
import styles from "./style.module.scss";

type CollectionsPageAllProps = {
  type:
    | "generic"
    | "unpaywalled"
    | "recommended"
    | "trending"
    | "all-countries"
    | "all-sectors"
    | "all-content-verticals"
    | "top-countries"
    | "top-sectors"
    | "top-content-verticals"
    | "followed";
  label?: string;
  hidePills?: boolean;
};

export const collectionsUrl = (
  pageIndex: number,
  filters?: {
    text?: string;
    isFeatured?: boolean;
    isFollowed?: boolean;
    isPublic?: boolean;
    segment?: string;
  },
  sortStr?: string
) => {
  const url = new ApiEndpoint(`/api/v2/collections`);

  if (sortStr) {
    url.searchParams.append("sort", sortStr);
  }

  url.searchParams.append("page[size]", "10");
  url.searchParams.append("page[number]", `${pageIndex}`);

  url.searchParams.append(
    "fields[collections]",
    "slug,name,description,cover-image-url,is-followed"
  );

  if (filters?.text) {
    url.searchParams.append("filter[fulltext]", filters?.text);
  }

  if (filters?.isFeatured) {
    url.searchParams.append("filter[is-featured]", "true");
  }

  if (filters?.isFollowed) {
    url.searchParams.append("filter[is-followed]", "true");
    if (sortStr) url.searchParams.delete("sort");
    url.searchParams.append("sort", "name");
  } else if (filters?.isPublic) {
    url.searchParams.append("filter[is-public]", "true");
  } else if (filters?.segment) {
    url.searchParams.append("filter[segment]", filters?.segment);
    if (!["trending", "recommended"].includes(filters?.segment)) {
      if (sortStr) url.searchParams.delete("sort");
      url.searchParams.append("sort", "name");
    }
  }

  return url.href;
};

export const sortOptions = [
  {
    id: "featured",
    name: "Featured",
    sortStr: "-is-featured,featured-position,-updated-at",
  },
  { id: "name", name: "A-Z", sortStr: "name" },
];

const CollectionsPageAll = ({
  type,
  label,
  hidePills,
}: CollectionsPageAllProps) => {
  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  const debouncedFilterTxt = useDebounce<string>(filterTxtValue, 300);
  const [sortOption, setSortOption] = useState(sortOptions[0]);

  const filters = useMemo(() => {
    if (type === "followed") {
      return { isFollowed: true };
    }

    if (type === "unpaywalled") {
      return { isPublic: true };
    }

    if (type !== "generic") {
      return { segment: type };
    }

    return {
      text: filterTxt,
    };
  }, [filterTxt, type]);

  const sortStr = useMemo(() => {
    if (type === "generic") {
      return sortOption.sortStr;
    }
  }, [sortOption.sortStr, type]);

  const { isRefreshing, infiniteList, setSize, size, hasMore } =
    useInfiniteList(
      (pageIndex) => {
        return collectionsUrl(pageIndex + 1, filters, sortStr);
      },
      {
        hasMore: ({ data }) =>
          ["generic", "followed"].includes(type) &&
          (!data?.length || !!data?.[data?.length - 1]?.data?.length),
        loadingIndicator: () => (
          <LoadingPlaceholder
            className={twMerge(
              "flex-wrap md:flex",
              "min-h-full",
              type === "generic" && "!mt-[20px]"
            )}
          />
        ),
        endIndicator: () =>
          ["generic"].includes(type) ? (
            <div className="text-center">No more collections</div>
          ) : (
            <></>
          ),
        emptyIndicator: () => (
          <div className={twMerge(styles.empty)}>
            {type === "generic" && (
              <EmptyState
                type="search"
                query={filterTxt}
                onClearClick={() => setFilterTxtValue("")}
              />
            )}
            {type === "followed" && <EmptyFollowedCollections style="cardV2" />}
          </div>
        ),
        scrollThreshold: 0.4,
        pagesRender: (data: any[]) => {
          let items =
            data?.reduce?.(
              (acc, pageData) => [...acc, ...(pageData?.data || [])],
              []
            ) || [];

          if (
            !["trending", "recommended", "unpaywalled", "generic"].includes(
              type
            )
          ) {
            items = items.sort((i1: any, i2: any) =>
              i1.name.localeCompare(i2.name)
            );
          }

          return (
            <div className={twMerge("flex flex-wrap gap-[30px] mt-[20px]")}>
              {items?.map?.((collection: any) => (
                <CollectionSnippet
                  key={collection.id}
                  collection={collection}
                  type="cardV3"
                />
              ))}
            </div>
          );
        },
      }
    );

  const filterPills = useMemo(() => {
    const { pills } =
      (AVAILABLE_TABS.find(
        (t) => t !== "separator" && t.segments.includes(type)
      ) as TabType) || {};

    if (!pills) return null;

    return (
      <Tabs>
        {pills.map((pill) => (
          <Tab
            key={pill.segment}
            href={`/collections?tab=${pill.segment}`}
            active={type === pill.segment}
          >
            {pill.name}
          </Tab>
        ))}
      </Tabs>
    );
  }, [type]);

  useEffect(() => {
    if (debouncedFilterTxt?.length >= 3) {
      setFilterTxt(debouncedFilterTxt);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [debouncedFilterTxt]);

  useEffect(() => {
    if (size <= 3 && hasMore) {
      setSize(size + 1);
    }
  }, [hasMore, setSize, size, type]);

  return (
    <>
      <div
        className={twMerge(
          styles.container,
          (type === "generic" || filterPills) && "mt-[-20px]",
          hidePills && "mt-[-10px]"
        )}
      >
        <div
          className={classNames(
            styles.filter,
            "flex flex-col space-y-[15px] w-full",
            "md:flex-row md:space-y-0 md:space-x-[30px]",
            "md:pt-[15px] md:px-[5px]",
            "mb-[8px] md:mt-0 md:mb-0"
          )}
        >
          {hidePills ? (
            <CollectionSectionLabel>{label}</CollectionSectionLabel>
          ) : (
            filterPills
          )}
          {type === "generic" && (
            <div className="flex flex-1">
              <div className="flex-1 w-full flex">
                <div
                  className={classNames(
                    styles.filterBox,
                    "flex-1 flex pb-[10px] mr-[15px] md:pb-0 md:mr-0",
                    "text-[14px] md:text-[15px]"
                  )}
                >
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="top-[5px] md:top-[5px]"
                  />
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      filterTxtError ? (
                        <Tooltip>{filterTxtError}</Tooltip>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <Input
                      className="flex-1 max-w-[350px] "
                      value={filterTxtValue}
                      autoComplete="false"
                      autoCapitalize="false"
                      onChange={(e) => setFilterTxtValue(e.target.value)}
                      placeholder="Search All Collections"
                      aria-label="filter"
                    />
                  </OverlayTrigger>
                </div>
              </div>
              <div className="flex md:items-center">
                <span
                  className={classNames(
                    styles.dropdown,
                    "-mr-[10px] md:mr-[10px]"
                  )}
                >
                  <Dropdown>
                    <Dropdown.Toggle variant="success" id="dropdown-basic">
                      {sortOption.name} <FontAwesomeIcon icon={faCaretDown} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className={styles.menu}>
                      {sortOptions.map((item) => (
                        <Dropdown.Item
                          className={classNames(
                            styles.item,
                            sortOption.id === item.id && styles.active
                          )}
                          key={item.id}
                          onClick={() => setSortOption(item)}
                        >
                          {item.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </span>
              </div>
            </div>
          )}
        </div>
        <div>
          {isRefreshing ? (
            <LoadingPlaceholder
              className={twMerge(
                "flex-wrap md:flex",
                "min-h-full",
                type === "generic" && "!mt-[20px]"
              )}
            />
          ) : (
            infiniteList
          )}
        </div>
      </div>
    </>
  );
};

export default CollectionsPageAll;
